/**
 * Interactive assessment configuration — the 6-step walkthrough that replaced the typing
 * chat (design handoff: "Interactive Assessment redesign"). Static config + pure helpers,
 * shared by the store (commit/scoring/answer-rendering) and the Live surface (rendering).
 *
 * Avery still hosts (the per-step `avery` framing line) and the live "Your profile" rail
 * animates from these `gains` for instant feedback. The authoritative scoring stays with
 * Avery's backend engine: each step's rendered answer (renderAnswer) is submitted to the
 * finalize endpoint, which routes evidence and produces the real Report. The reveal/insight
 * copy here is representative of the demo persona (the handoff notes this).
 */

export type SkillKey = 'prompt' | 'technical' | 'practical' | 'critical' | 'workflow';
export type Skills = Record<SkillKey, number>;

export const SKILL_KEYS: SkillKey[] = ['prompt', 'technical', 'practical', 'critical', 'workflow'];
export const SKILL_LABELS: Record<SkillKey, string> = {
  prompt: 'Prompt mastery',
  technical: 'Technical',
  practical: 'Practical',
  critical: 'Critical eval',
  workflow: 'Workflow design',
};

/** The user's baseline, so the profile rail isn't empty at the start. */
export const INITIAL_SKILLS: Skills = { prompt: 1.8, technical: 1.5, practical: 2.7, critical: 1.5, workflow: 0.4 };

export interface Band {
  label: string;
  bg: string;
  ink: string;
}

/** Skill level (0–5) → band tag. Thresholds + colours per the handoff design tokens. */
export function skillBand(v: number): Band {
  if (v >= 4.5) return { label: 'Transformative', bg: '#00D700', ink: '#000' };
  if (v >= 3.5) return { label: 'Advanced', bg: '#C2FF01', ink: '#000' };
  if (v >= 2.5) return { label: 'Capable', bg: '#B52CF2', ink: '#fff' };
  if (v >= 1.5) return { label: 'Developing', bg: '#2438E6', ink: '#fff' };
  return { label: 'Early', bg: '#6E7180', ink: '#fff' };
}

// ---- Step types ----
interface ScenarioOption {
  id: string;
  label: string;
  sub: string;
  react: string;
}
interface ScenarioStep {
  kind: 'scenario';
  eyebrow: string;
  avery: string;
  situation: string;
  q: string;
  options: ScenarioOption[];
  gains: Partial<Skills>;
}
interface PromptStep {
  kind: 'prompt';
  eyebrow: string;
  avery: string;
  base: string;
  clauses: { key: string; label: string; clause: string }[];
  insight: string;
  gains: Partial<Skills>;
}
interface RankStep {
  kind: 'rank';
  eyebrow: string;
  avery: string;
  items: string[];
  insight: string;
  gains: Partial<Skills>;
}
interface SliderStep {
  kind: 'slider';
  eyebrow: string;
  avery: string;
  gains: Partial<Skills>;
}
interface OpenStep {
  kind: 'open';
  eyebrow: string;
  avery: string;
  chips: string[];
  insight: string;
  gains: Partial<Skills>;
}
export type Step = ScenarioStep | PromptStep | RankStep | SliderStep | OpenStep;

export const STEPS: Step[] = [
  {
    kind: 'scenario',
    eyebrow: 'A recent task',
    avery: "Let's start with something real. No trick questions — I just want to see how you actually work.",
    situation: "It's 9am. You've got an important task due by 10:30 that needs you to pull together some background and then produce something from it.",
    q: "What's your honest first move?",
    options: [
      { id: 'manual', label: 'Gather and read all the background material myself', sub: 'Thorough, manual', react: 'Classic thorough prep — but that reading is exactly the kind of gather AI can compress, freeing you for the judgement calls.' },
      { id: 'aifirst', label: 'Ask AI to summarise the background, then refine it myself', sub: 'AI-first gather', react: "That's high-leverage. You're using AI for the gather and keeping the thinking for yourself — your practical-application signal is strong." },
      { id: 'wing', label: 'Skim the highlights and work the rest out as I go', sub: 'Lean and fast', react: 'Fast, but risky when the stakes are high. A two-minute AI brief would de-risk it for almost no cost.' },
    ],
    gains: { practical: 0.6, workflow: 0.5, prompt: 0.2 },
  },
  {
    kind: 'prompt',
    eyebrow: 'Build a sharper ask',
    avery: "Here's a prompt someone might use for that brief. Make it sharper — tap what you'd add.",
    base: 'Summarise this background for my upcoming task.',
    clauses: [
      { key: 'role', label: 'Add a role', clause: 'Act as an expert assistant briefing me.' },
      { key: 'context', label: 'Give it context', clause: 'Use the attached notes and source material.' },
      { key: 'shape', label: 'Set the output shape', clause: 'Return 5 bullets plus 3 questions to dig into.' },
      { key: 'aud', label: 'Name the audience', clause: 'Keep it for my eyes, not the final audience.' },
      { key: 'check', label: 'Ask it to self-check', clause: "Flag anything you're unsure about." },
    ],
    gains: { prompt: 0.9, critical: 0.3 },
    insight: 'Every clause you added is a fluency signal — role, context and output shape are the three that change the result the most. Reaching for them instinctively is the mark of a confident operator.',
  },
  {
    kind: 'rank',
    eyebrow: 'Where AI helps most',
    avery: 'Order these prep steps so the ones AI should own sit at the top.',
    items: ['Research the relevant background', 'Pull together the source material', 'Draft the first version', 'Write the key questions to resolve', 'Decide the overall approach'],
    gains: { workflow: 0.7, practical: 0.3 },
    insight: 'Most high performers push gather and first-draft to the top and keep strategy and the key decisions for themselves — close to how you ranked it. That instinct for what to delegate is workflow design in action.',
  },
  {
    kind: 'slider',
    eyebrow: 'Your trust dial',
    avery: "Be honest here — there's no right answer.",
    gains: { critical: 0.6, prompt: 0.2 },
  },
  {
    kind: 'scenario',
    eyebrow: 'Spot the risk',
    avery: 'Now a judgement call — the kind that separates careful operators from the rest.',
    situation: "An AI-drafted document makes a specific factual claim about your product or service. You're not certain it's actually true.",
    q: 'What do you do before it goes out?',
    options: [
      { id: 'verify', label: 'Check it against our docs / source of truth', sub: 'Verify first', react: "Exactly the habit that keeps AI safe at the edge. Strong critical evaluation — you don't let unverified claims through." },
      { id: 'soften', label: "Soften the wording so it's not a hard claim", sub: 'Hedge it', react: "Pragmatic — but the claim's truth still matters to whoever reads it. Verifying beats hedging when it's a factual claim." },
      { id: 'trust', label: 'Send it — the model is usually right', sub: 'Trust the draft', react: 'This is the classic over-trust trap. Hallucinated facts on things like capabilities and figures are where AI gets teams into real trouble.' },
    ],
    gains: { critical: 0.6, technical: 0.5 },
  },
  {
    kind: 'open',
    eyebrow: 'One thing to keep',
    avery: "Last one. In a few words — what's the one task you'd hand to AI tomorrow if you trusted it completely?",
    chips: ['First-draft summaries', 'Research & background briefs', 'Routine document updates'],
    gains: { practical: 0.4, workflow: 0.4 },
    insight: "That's your highest-leverage handoff — and it's exactly what your report will build a 12-week plan around. You finished with a clear sense of where AI earns its place in your week.",
  },
];

/** The "minutes left" copy per step (shrinks as the user progresses). Calibrated to the
 *  real pace of the 6-tap quick walkthrough (~5 min), not the deeper conversational mode. */
export const TIME_LEFT = ['~5 min left', '~4 min left', '~3 min left', '~2 min left', '~1 min left', 'Almost done'];

export function promptQuality(count: number): { pct: number; label: string } {
  const pct = 12 + (count / 5) * 88;
  const label = count === 0 ? 'Bare prompt' : count <= 2 ? 'Getting sharper' : count <= 4 ? 'Strong prompt' : 'Excellent prompt';
  return { pct, label };
}

export function sliderZone(v: number): { label: string; reaction: string; reveal: string } {
  if (v < 34)
    return {
      label: 'I rewrite almost everything',
      reaction: 'You keep a tight grip on the final word.',
      reveal: 'Keeping the final word is a real safeguard — pair it with delegating the gather so you are not rewriting from scratch every time.',
    };
  if (v <= 66)
    return {
      label: 'I edit the framing & the facts',
      reaction: 'You trust the draft but own the judgement — the sweet spot.',
      reveal: 'That edit-the-framing instinct is the sweet spot: high trust on the draft, your judgement on what actually matters.',
    };
  return {
    label: 'Light touch, then send',
    reaction: 'High trust — best paired with a quick verification habit.',
    reveal: 'A light touch keeps you fast — anchor it with one quick verification step so speed never costs you accuracy.',
  };
}

/** Working state for the in-progress step (mirrors the store fields). */
export interface StepWorking {
  choice: string | null;
  promptSel: Record<string, boolean>;
  rankOrder: string[] | null;
  sliderVal: number;
  openText: string;
}

/** Render the committed answer as the natural-language statement sent to Avery's scorer. */
export function renderAnswer(step: Step, w: StepWorking): string {
  switch (step.kind) {
    case 'scenario':
      return step.options.find((o) => o.id === w.choice)?.label ?? '';
    case 'prompt': {
      const selected = step.clauses.filter((c) => w.promptSel[c.key]).map((c) => c.clause);
      return [step.base, ...selected].join(' ');
    }
    case 'rank':
      return `Where AI should take the most off my plate, in order: ${(w.rankOrder ?? step.items).join('; ')}.`;
    case 'slider': {
      const z = sliderZone(w.sliderVal);
      return `On trusting AI output: ${z.label}. ${z.reaction}`;
    }
    case 'open':
      return w.openText.trim();
  }
}

/** The reveal/insight text shown after committing a step. */
export function revealText(step: Step, w: StepWorking): string {
  switch (step.kind) {
    case 'scenario':
      return step.options.find((o) => o.id === w.choice)?.react ?? '';
    case 'slider':
      return sliderZone(w.sliderVal).reveal;
    case 'prompt':
    case 'rank':
    case 'open':
      return step.insight;
  }
}

/** Gain chips for the reveal panel — one per skill the step moved. */
export function gainChips(step: Step): { label: string; delta: number }[] {
  return SKILL_KEYS.filter((k) => step.gains[k]).map((k) => ({ label: SKILL_LABELS[k], delta: step.gains[k] as number }));
}

// ---- Role tailoring (Avery rewrites the content per role; structure/gains stay fixed) ----
/** The tailored CONTENT from the backend; merged onto STEPS so kind/gains/eyebrow stay fixed. */
export interface TailoredPack {
  task: { avery: string; situation: string; q: string; options: { label: string; sub: string; react: string }[] };
  prompt: { avery: string; base: string; clauses: { label: string; clause: string }[]; insight: string };
  rank: { avery: string; items: string[]; insight: string };
  risk: { avery: string; situation: string; q: string; options: { label: string; sub: string; react: string }[] };
  keep: { avery: string; chips: string[]; insight: string };
}

/**
 * Overlay tailored content onto the fixed STEPS skeleton. kind, eyebrow, and gains (the
 * scoring weights) never come from the model — only surface text — so scoring is unaffected
 * and structure can't break. Option ids / clause keys are assigned positionally here.
 */
export function mergeTailored(pack: TailoredPack): Step[] {
  return STEPS.map((s): Step => {
    switch (s.kind) {
      case 'scenario': {
        const src = s.eyebrow === 'Spot the risk' ? pack.risk : pack.task;
        return {
          ...s,
          avery: src.avery,
          situation: src.situation,
          q: src.q,
          options: src.options.map((o, i) => ({ id: `opt${i}`, label: o.label, sub: o.sub, react: o.react })),
        };
      }
      case 'prompt':
        return {
          ...s,
          avery: pack.prompt.avery,
          base: pack.prompt.base,
          clauses: pack.prompt.clauses.map((c, i) => ({ key: `c${i}`, label: c.label, clause: c.clause })),
          insight: pack.prompt.insight,
        };
      case 'rank':
        return { ...s, avery: pack.rank.avery, items: pack.rank.items, insight: pack.rank.insight };
      case 'open':
        return { ...s, avery: pack.keep.avery, chips: pack.keep.chips, insight: pack.keep.insight };
      case 'slider':
        return s; // role-agnostic — left as-is
    }
  });
}
